import { Check } from "lucide-react";
import { CTAButton } from "@/components/CTAButton";
import { useAudit } from "@/components/AuditContext";

// ─── Grille tarifaire (HT / mois) ──────────────────────────────────────────
// Un seul endroit à modifier pour changer les prix affichés sur le site.
const FORFAITS = [
  { key: "essentiel", name: "Essentiel", postes: "De 1 à 3 postes", price: "397 €", featured: false },
  { key: "equipe", name: "Équipe", postes: "De 4 à 6 postes", price: "497 €", featured: true },
  { key: "pro", name: "Pro", postes: "De 7 à 9 postes", price: "697 €", featured: false },
];

const PC_OPTION_PRICE = "+60 € HT / poste / mois";

const includedBullets = [
  "Fibre professionnelle avec secours 4G",
  "Pare-feu, routeur, Wi-Fi et switch installés",
  "Protection de chaque ordinateur, surveillée 24h/24",
  "Sauvegarde des ordinateurs et de la messagerie",
  "Messagerie, gestion des mots de passe et clés de sécurité",
  "Téléphonie professionnelle",
  "Sensibilisation de vos équipes aux arnaques",
  "Support illimité et maintenance",
];

const highlights = [
  "Zéro frais de mise en service",
  "Un seul prélèvement par mois",
  "Un seul interlocuteur",
];

/**
 * Grille tarifaire : 3 forfaits tout compris selon la taille de l'équipe,
 * + option renouvellement des ordinateurs. Utilisée sur la home et sur /offres.
 * Rendu sur fond navy (texte clair).
 */
export const PricingSelector = ({ id = "tarifs" }) => {
  const { openAudit } = useAudit();

  return (
    <div id={id} className="relative max-w-5xl mx-auto">
      {/* Les 3 forfaits */}
      <div className="grid md:grid-cols-3 gap-5 items-stretch" data-testid="pricing-forfaits">
        {FORFAITS.map((f) => (
          <div
            key={f.key}
            className={
              f.featured
                ? "relative rounded-2xl border border-amber-500/60 bg-gradient-to-b from-amber-500/[0.14] to-white/[0.02] p-7 sm:p-8 text-center md:-my-3 md:py-11"
                : "relative rounded-2xl border border-white/12 bg-white/[0.03] p-7 sm:p-8 text-center"
            }
            data-testid={`pricing-card-${f.key}`}
          >
            {f.featured && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-amber-500 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-navy-dark whitespace-nowrap">
                Le plus choisi
              </span>
            )}
            <p className="text-amber-500 text-xs uppercase tracking-widest font-medium">{f.name}</p>
            <p className="mt-2 text-white/80 text-sm">{f.postes}</p>
            <div className="mt-5 flex items-end justify-center gap-2">
              <span className="font-display text-5xl sm:text-6xl font-bold tracking-tight text-white" data-testid={`price-${f.key}`}>
                {f.price}
              </span>
            </div>
            <p className="mt-2 text-white/60 text-sm">HT / mois, tout compris</p>
            <CTAButton
              className="mt-7 w-full"
              variant={f.featured ? undefined : "ghostLight"}
              onClick={openAudit}
              data-testid={`pricing-cta-${f.key}`}
            >
              Parlons-en
            </CTAButton>
          </div>
        ))}
      </div>

      {/* Points forts communs */}
      <div className="mt-10 flex flex-wrap justify-center gap-x-8 gap-y-3">
        {highlights.map((h) => (
          <span key={h} className="flex items-center gap-2 text-white font-medium text-sm">
            <Check className="w-4 h-4 text-amber-500 shrink-0" />
            {h}
          </span>
        ))}
      </div>

      {/* Ce qui est inclus dans tous les forfaits */}
      <div className="mt-10 rounded-2xl border border-white/12 bg-white/[0.03] p-6 sm:p-8" data-testid="pricing-included">
        <p className="text-white font-display text-lg font-medium">Inclus dans tous les forfaits</p>
        <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
          {includedBullets.map((it) => (
            <li key={it} className="flex items-start gap-3 text-white/85 text-sm">
              <Check className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Option ordinateurs */}
      <div className="mt-5 rounded-2xl border border-white/12 bg-white/[0.03] p-6 sm:p-8" data-testid="hardware-option">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <p className="text-white font-display text-lg font-medium">Option — Ordinateurs renouvelés tous les 3 ans</p>
          <p className="text-amber-400 font-semibold shrink-0">{PC_OPTION_PRICE}</p>
        </div>
        <p className="mt-3 text-white/70 text-sm leading-relaxed">
          Ordinateur professionnel et périphériques fournis, garantis 3 ans, puis remplacés par du neuf. Aucun achat important à prévoir.
        </p>
      </div>

      <p className="text-center text-white/55 text-xs mt-8">
        Tarifs HT par mois. Engagement de 3 ans. Matériel financé par notre partenaire bancaire.
      </p>

      {/* Plus de 9 postes */}
      <p className="text-center text-white/60 text-sm mt-4">
        Plus de 9 postes ?{" "}
        <button
          onClick={openAudit}
          className="text-amber-400 font-medium underline underline-offset-2 hover:text-amber-300 transition-colors"
          data-testid="pricing-devis-cta"
        >
          Demandez un devis sur mesure
        </button>
        .
      </p>
    </div>
  );
};
